import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { EntitySelect } from '@/components/EntitySelect';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { CURRENCIES, type Currency } from '@/features/crm/deals/constants';
import { getService } from '@/features/finance/services/api';
import { resolveServiceLabel, searchServices } from '@/features/finance/services/select';
import { useAddProposalLine } from '../queries';
import type { AddLineInput } from '../types';

const schema = z.object({
  service: z.string(),
  description: z.string().trim().min(1, 'Description is required'),
  quantity: z.string().refine((v) => Number(v) > 0, 'Must be > 0'),
  unit_rate: z.string().refine((v) => v !== '' && Number(v) >= 0, 'Enter a rate'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
  fx_rate_to_base: z.string().refine((v) => Number(v) > 0, 'Must be > 0'),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['service', 'description', 'quantity', 'unit_rate', 'currency', 'fx_rate_to_base'] as const;

interface AddLineFormProps {
  proposalId: number;
  defaultCurrency: Currency;
}

export function AddLineForm({ proposalId, defaultCurrency }: AddLineFormProps) {
  const addLine = useAddProposalLine(proposalId);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      service: '',
      description: '',
      quantity: '1',
      unit_rate: '',
      currency: defaultCurrency,
      fx_rate_to_base: '1',
    },
  });

  // Picking a catalog service prefills name + USD rate (still editable, and
  // optional — leave it empty for an ad-hoc line).
  async function onServiceChange(value: string) {
    form.setValue('service', value);
    if (!value) return;
    try {
      const svc = await getService(Number(value));
      form.setValue('description', svc.name, { shouldValidate: true });
      form.setValue('unit_rate', svc.base_rate, { shouldValidate: true });
      form.setValue('currency', 'USD');
      form.setValue('fx_rate_to_base', '1');
    } catch {
      /* leave fields as-is if the lookup fails */
    }
  }

  async function handleSubmit(values: FormValues) {
    const input: AddLineInput = {
      service: values.service ? Number(values.service) : null,
      description: values.description,
      quantity: values.quantity,
      unit_rate: values.unit_rate,
      currency: values.currency,
      fx_rate_to_base: values.fx_rate_to_base,
    };
    try {
      await addLine.mutateAsync(input);
      form.reset({ ...form.getValues(), service: '', description: '', unit_rate: '' });
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Service (optional)</FormLabel>
              <FormControl>
                <EntitySelect
                  queryKey="proposal-line-service-picker"
                  value={field.value}
                  onChange={onServiceChange}
                  search={searchServices}
                  resolveLabel={resolveServiceLabel}
                  placeholder="Pick from catalog, or type a line below"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="Senior engineer — 40h" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-4">
          <FormField
            control={form.control}
            name="quantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Qty</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.01" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="unit_rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit rate</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.01" placeholder="0.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="fx_rate_to_base"
            render={({ field }) => (
              <FormItem>
                <FormLabel>FX → USD</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.00000001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <Button type="submit" size="sm" disabled={addLine.isPending}>
          <Plus /> {addLine.isPending ? 'Adding…' : 'Add line'}
        </Button>
      </form>
    </Form>
  );
}
